// @flow
import type {
  Platform,
  Server,
  Client,
  Reader,
  AvailableSettings,
  SettingType,
  FileComponents,
  Toggles,
} from 'docs/data/integration-builder';
import * as React from 'react';
import {values, union, flatten} from 'lodash';

import {loadFiles, loadSteps, getPlatform} from 'docs/util';

type GetSettingsProps = {|
  conditions: {
    platform: ?Platform,
    client: ?Client,
    server: ?Server,
    toggles: Toggles,
  },
  settings: AvailableSettings,
|};
type GetSettingsResult = {|
  platform: Platform,
  client: Client,
  server: Server,
|};

const getSettings = ({
  conditions,
  settings,
}: GetSettingsProps): GetSettingsResult => {
  const server =
    conditions.server && settings.servers.includes(conditions.server)
      ? conditions.server
      : settings.servers[0];
  let platform = conditions.platform;
  let client = conditions.client;

  if (
    !platform ||
    !client ||
    !settings.platforms.includes(platform) ||
    !getPlatform(settings.clients, client)
  ) {
    platform = settings.platforms[0];
    client = settings.clients[platform][0];
  }

  return {platform, client, server};
};

type LoadProps = {|
  type?: SettingType,
  conditions: {
    platform: ?Platform,
    client: ?Client,
    server: ?Server,
    toggles: Toggles,
    reader: ?Reader,
  },
  settings: AvailableSettings,
  children: Array<React.Element<any>>,
|};

export type LoadResult = {|
  files: FileComponents,
  steps: ?React.Element<any>,
|};

export const loadForServerOnly = ({
  conditions,
  settings,
  children,
}: LoadProps): LoadResult => {
  const server =
    conditions.server && settings.servers.includes(conditions.server)
      ? conditions.server
      : settings.servers[0];
  const fileConditions = {toggles: conditions.toggles};

  const files = loadFiles(
    [`server/${server}`, `build/${server}`, 'readme'],
    fileConditions,
    children,
  );
  return {
    files,
    steps: loadSteps('all', children),
  };
};

export const loadForDefault = ({
  conditions,
  settings,
  children,
}: LoadProps): LoadResult => {
  const {platform, client, server} = getSettings({
    conditions,
    settings,
  });
  const fileConditions = {toggles: conditions.toggles};
  const files = loadFiles(
    [`server/${server}`, `${platform}/${client}`, `build/${server}`, 'readme'],
    fileConditions,
    children,
  );

  return {
    files,
    steps: loadSteps(platform, children),
  };
};

export const loadForReact = ({
  conditions,
  settings,
  children,
}: LoadProps): LoadResult => {
  const files = loadFiles(['react'], {}, children);
  const steps = loadSteps('react', children);
  return {
    files,
    steps,
  };
};

export const loadForWebWithNext = ({
  conditions,
  settings,
  children,
}: LoadProps): LoadResult => {
  const {platform, client, server} = getSettings({
    conditions,
    settings,
  });

  let files = {};
  const fileConditions = {toggles: conditions.toggles};
  if (client !== 'next') {
    // Only load the appropriate server file if it is not Next.js
    // Since the Next.js integration only works with a specific server integration
    files = loadFiles(
      [
        `server/${server}`,
        `${platform}/${client}`,
        `build/${server}`,
        `readme-main`,
      ],
      fileConditions,
      children,
    );
  } else {
    files = loadFiles(
      [`${platform}/${client}`, `readme-next`],
      fileConditions,
      children,
    );
  }

  return {
    files,
    steps: loadSteps(platform, children),
  };
};

export const loadForTerminal = ({
  conditions,
  settings,
  children,
}: LoadProps): LoadResult => {
  const server =
    conditions.server && settings.servers.includes(conditions.server)
      ? conditions.server
      : settings.servers[0];
  let platform = conditions.platform;
  let client = conditions.client;

  // Get the set of valid platforms (e.g. web, ios) for each reader and remove any duplicates
  const platforms = union(flatten(values(settings.platformsForReader || {})));

  if (
    !platform ||
    !client ||
    !platforms.includes(platform) ||
    !getPlatform(settings.clients, client)
  ) {
    platform = platforms[0];
    client = settings.clients[platform][0];
  }

  const fileConditions = {
    reader: conditions.reader,
    platform: conditions.platform,
  };
  const files = loadFiles(
    [`server/${server}`, `${platform}/${client}`, `build/${server}`, `readme`],
    fileConditions,
    children,
  );

  return {
    files,
    steps: loadSteps(platform, children),
  };
};

export const load = ({
  type,
  conditions,
  settings,
  children,
}: LoadProps): LoadResult => {
  switch (type) {
    case 'server-only':
      return loadForServerOnly({conditions, settings, children});
    case 'web-only':
      return loadForDefault({conditions, settings, children});
    case 'web-with-next':
      return loadForWebWithNext({conditions, settings, children});
    case 'terminal':
      return loadForTerminal({conditions, settings, children});
    case 'react-only':
      return loadForReact({conditions, settings, children});
    default:
      return loadForDefault({conditions, settings, children});
  }
};
